<template>
  <BaseButton
    color="inverted"
    :options="popoverOptions"
    icon="settings"
    @select="handleListItemClick"
    v-test="'manage-subscription-link'"
  />
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { IconName } from '@/components/base-icon/types';
import { useStripe } from '@/helpers/stripe';
import onUpgradeButtonClick from '@/modules/_shared/subscription/on-upgrade-button-click';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import { SubscriptionPlan } from '@/types';
import getCompanySubscription from './get-company-subscription';
import { useCurrentPlanQuote } from './store';

type PopoverOption = {
  label: string;
  value: string;
  icon: IconName;
};

const { t } = useI18n();
const { goToStripePortal } = useStripe();
const { isSubscribed, isProlightMarket } = useCompanyStore();
const { activeSubscription } = getCompanySubscription();
const { quote } = storeToRefs(useCurrentPlanQuote());
const { user, hasFeatureFlag } = useUserStore();
const { handleUpgradeButtonClick } = onUpgradeButtonClick();

const canUpgradeSubscription = computed(
  () =>
    quote.value &&
    quote.value.plan !== SubscriptionPlan.Prolight &&
    !(quote.value.plan === SubscriptionPlan.Pro && !isProlightMarket) &&
    !activeSubscription.value?.cancelled,
);

const canCancelSubscription = computed(
  () =>
    isSubscribed &&
    user?.owner &&
    activeSubscription.value &&
    !activeSubscription.value.cancelled &&
    !hasFeatureFlag('subscription-frozen'),
);

const popoverOptions: ComputedRef<PopoverOption[]> = computed(() => {
  const result = [];

  if (canUpgradeSubscription.value) {
    const managePlanOption: PopoverOption = {
      label: t('admin.cards.plan.manage_plan'),
      value: 'manage_plan',
      icon: 'trending',
    };

    result.push(managePlanOption);
  }

  const editOption: PopoverOption = {
    label: t('admin.cards.plan.edit_payment_method'),
    value: 'edit_payment_method',
    icon: 'edit',
  };

  result.push(editOption);

  if (canCancelSubscription.value) {
    const cancelOption: PopoverOption = {
      label: t('admin.cards.plan.cancel_subscription'),
      value: 'cancel_subscription',
      icon: 'close',
    };

    result.push(cancelOption);
  }

  return result;
});

const router = useRouter();

const handleListItemClick = (option: string) => {
  switch (option) {
    case 'manage_plan':
      handleUpgradeButtonClick();
      break;
    case 'edit_payment_method':
      goToStripePortal();
      break;
    case 'cancel_subscription':
      router.push({ name: 'admin-billing-cancel' });
      break;
    default:
      break;
  }
};
</script>
